import React, { useState, useRef } from 'react';
import axios from 'axios';
import { FiUpload, FiX, FiTrash2 } from 'react-icons/fi';
import DeleteConfirmationModal from './DeleteConfirmationModal.jsx';

const API_BASE = process.env.REACT_APP_API_URL || '';

/** mediaType → URL path folder (wechatQR, sp, icp, sv, icv) — same as R2 folder names */
const MEDIA_TYPE_TO_FOLDER = {
    wechatQR: 'w',
    standardPhotos: 'sp',
    icecreamPhotos: 'icp',
    standardVideos: 'sv',
    icecreamVideos: 'icv',
};

/** Proxy URL so frontend/inspect only shows API path + folder + filename (e.g. .../media/sp/788sp090220261.jpg), not R2 domain. */
function getMediaDisplayUrl(memberId, mediaType, fullUrl, apiResource = 'godmembers') {
    if (!memberId || !mediaType || !fullUrl) return fullUrl || '';
    const folder = MEDIA_TYPE_TO_FOLDER[mediaType] || mediaType;
    const filename = fullUrl.split('/').pop() || '';
    if (!filename) return fullUrl;
    const base = API_BASE ? API_BASE.replace(/\/$/, '') : '';
    return `${base}/api/${apiResource}/${memberId}/media/${folder}/${encodeURIComponent(filename)}`;
}

/**
 * Drag & drop + file picker for god member media (wechatQR, standardPhotos, icecreamPhotos, standardVideos, icecreamVideos).
 * - memberId set: upload/delete via API (edit mode).
 * - memberId null: collect pending files for create (parent uploads after create).
 */
const MediaUploadSection = ({
    label,
    mediaType,
    urls = [],
    onUrlsChange,
    pendingFiles = [],
    onPendingFilesChange,
    memberId,
    apiResource = 'godmembers',
    accept = 'image/*', // 'image/*' for photos/QR, 'video/*' for videos
    multiple = true,
    disabled = false,
}) => {
    const [dragOver, setDragOver] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [lightboxSrc, setLightboxSrc] = useState(null); // click image to show big for right-click copy
    const [deleteAllConfirmOpen, setDeleteAllConfirmOpen] = useState(false);
    const [deleteAllLoading, setDeleteAllLoading] = useState(false);
    const inputRef = useRef(null);

    const isEditMode = !!memberId;

    const handleFiles = async (files) => {
        if (!files || files.length === 0) return;
        const list = Array.from(files);
        if (!isEditMode) {
            onPendingFilesChange && onPendingFilesChange([...pendingFiles, ...list]);
            return;
        }
        setError('');
        setUploading(true);
        try {
            const formData = new FormData();
            list.forEach((f) => formData.append('files', f));
            formData.append('mediaType', mediaType);
            const token = localStorage.getItem('token');
            const isVideoUpload = mediaType === 'standardVideos' || mediaType === 'icecreamVideos';
            const res = await axios.post(
                `${API_BASE}/api/${apiResource}/${memberId}/media`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true',
                    },
                    timeout: isVideoUpload ? 5 * 60 * 1000 : 60000,
                }
            );
            if (res.data.success && res.data.data && Array.isArray(res.data.data[mediaType])) {
                onUrlsChange && onUrlsChange(res.data.data[mediaType]);
            }
        } catch (err) {
            let msg = err.response?.data?.error || err.message || 'Upload failed';
            if (err.response?.status === 413 || err.message?.includes('413') || msg?.toLowerCase().includes('too large')) {
                msg = 'File too large (413). Video uploads need the server reverse proxy (e.g. nginx) to allow large bodies: set client_max_body_size 500m; then reload.';
            } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
                msg = 'Upload timed out. Video conversion can take several minutes; try again or use a smaller file.';
            }
            setError(msg);
        } finally {
            setUploading(false);
        }
    };

    const handleRemoveUrl = async (url) => {
        if (!isEditMode) return;
        setError('');
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${API_BASE}/api/${apiResource}/${memberId}/media`,
                {
                    params: { mediaType, url },
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true',
                    },
                }
            );
            onUrlsChange && onUrlsChange(urls.filter((u) => u !== url));
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Delete failed');
        }
    };

    const handleRemovePending = (index) => {
        const next = pendingFiles.filter((_, i) => i !== index);
        onPendingFilesChange && onPendingFilesChange(next);
    };

    const handleConfirmDeleteAllInCategory = async () => {
        if (!isEditMode || !urls.length) {
            setDeleteAllConfirmOpen(false);
            return;
        }
        setError('');
        setDeleteAllLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.delete(`${API_BASE}/api/${apiResource}/${memberId}/media`, {
                data: { mediaType, deleteAll: true },
                headers: {
                    Authorization: `Bearer ${token}`,
                    'ngrok-skip-browser-warning': 'true',
                },
            });
            if (res.data?.success) {
                const next = res.data.data && Array.isArray(res.data.data[mediaType])
                    ? res.data.data[mediaType]
                    : [];
                onUrlsChange && onUrlsChange(next);
                setDeleteAllConfirmOpen(false);
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Delete all failed');
        } finally {
            setDeleteAllLoading(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        if (disabled) return;
        handleFiles(e.dataTransfer.files);
    };

    const onDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const onDragLeave = () => setDragOver(false);

    const isVideo = mediaType === 'standardVideos' || mediaType === 'icecreamVideos';

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
                <label className="block text-sm font-medium text-gray-700">{label}</label>
                {isEditMode && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (urls.length > 0) setDeleteAllConfirmOpen(true);
                        }}
                        disabled={!urls.length || deleteAllLoading}
                        className="p-1.5 rounded-md text-gray-500 hover:text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:pointer-events-none shrink-0"
                        title={urls.length ? `Delete all ${label}` : 'Nothing to delete'}
                        aria-label={`Delete all ${label}`}
                    >
                        <FiTrash2 className="w-4 h-4" />
                    </button>
                )}
            </div>
            <div
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                    dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'
                } ${disabled ? 'opacity-60 pointer-events-none' : 'cursor-pointer'}`}
                onClick={() => !disabled && inputRef.current && inputRef.current.click()}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    multiple={multiple}
                    className="hidden"
                    onChange={(e) => {
                        handleFiles(e.target.files);
                        e.target.value = '';
                    }}
                />
                <FiUpload className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-1 text-sm text-gray-600">
                    Drag & drop or click to upload {isVideo ? 'videos' : 'images'}
                </p>
                {!isEditMode && (
                    <p className="mt-0.5 text-xs text-amber-600">Save member first to upload to cloud</p>
                )}
            </div>
            {uploading && <p className="text-sm text-blue-600">Uploading…</p>}
            {error && <p className="text-sm text-red-600">{error}</p>}

            {/* Existing URLs (edit mode) — use proxy URL so inspect shows only API path + filename */}
            {urls.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                    {urls.map((url) => {
                        const displaySrc = isEditMode ? getMediaDisplayUrl(memberId, mediaType, url, apiResource) : url;
                        return (
                        <div key={url} className="relative group">
                            {isVideo ? (
                                <video
                                    src={displaySrc}
                                    className="w-24 h-24 object-cover rounded border border-gray-200"
                                    muted
                                    preload="metadata"
                                />
                            ) : (
                                <img
                                    src={displaySrc}
                                    alt=""
                                    className="w-24 h-24 object-cover rounded border border-gray-200 cursor-pointer hover:ring-2 hover:ring-blue-400"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setLightboxSrc(displaySrc);
                                    }}
                                    title="Click to open full size (right-click to copy image)"
                                />
                            )}
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveUrl(url);
                                }}
                                className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full opacity-90 group-hover:opacity-100"
                            >
                                <FiX className="w-4 h-4" />
                            </button>
                        </div>
                    ); })}
                </div>
            )}

            {/* Pending files (create mode) */}
            {!isEditMode && pendingFiles.length > 0 && (
                <ul className="mt-2 space-y-1 text-sm text-gray-700">
                    {pendingFiles.map((file, i) => (
                        <li key={i} className="flex items-center gap-2">
                            <span className="truncate flex-1">{file.name}</span>
                            <button
                                type="button"
                                onClick={() => handleRemovePending(i)}
                                className="text-red-600 hover:text-red-800 shrink-0"
                            >
                                <FiX className="w-4 h-4" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {/* Lightbox: click image to open big for right-click copy */}
            {deleteAllConfirmOpen && (
                <DeleteConfirmationModal
                    title={`Delete all ${label}?`}
                    message="This removes every file in this category from cloud storage (R2) and clears it on the member record. This cannot be undone."
                    itemName={label}
                    isLoading={deleteAllLoading}
                    onCancel={() => !deleteAllLoading && setDeleteAllConfirmOpen(false)}
                    onConfirm={handleConfirmDeleteAllInCategory}
                />
            )}

            {lightboxSrc && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4"
                    onClick={() => setLightboxSrc(null)}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Image preview"
                >
                    <img
                        src={lightboxSrc}
                        alt="Full size preview"
                        className="max-w-full max-h-[90vh] w-auto h-auto object-contain rounded shadow-lg"
                        onClick={(e) => e.stopPropagation()}
                        draggable={false}
                        style={{ pointerEvents: 'auto' }}
                    />
                    <button
                        type="button"
                        onClick={() => setLightboxSrc(null)}
                        className="absolute top-4 right-4 p-2 rounded-full bg-white/90 text-gray-800 hover:bg-white"
                        aria-label="Close"
                    >
                        <FiX className="w-6 h-6" />
                    </button>
                </div>
            )}
        </div>
    );
};

export default MediaUploadSection;
